import { useState } from "react";
import { Check, Plus, Search, Trash2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import { Button } from "ui/button";
import { Card, CardContent } from "ui/card";
import { Input } from "ui/input";
import { deleteShortlistedApplicant } from "services/tourApprovalService";

interface ApprovedApplicant {
  id: string;
  name: string;
  itsNumber: string;
  gender: string;
  age: number;
  status: string;
  photo: string;
  hofITS: string;
  applicantType: string;
  passportNo: string;
  nationality: string;
  needsAccommodation: boolean;
}

interface ShortlistedApplicant extends ApprovedApplicant {
  publicId: string;
}

interface RoomsAllocationProps {
  onAddApplicants: (applicantIds: string[]) => void;
  approvedApplicants: ApprovedApplicant[];
  shortlistedApplicants: ShortlistedApplicant[];
  reloadShortlistedApplicants: () => Promise<void>;
  isReadOnly?: boolean;
}

function GenderAvatar({
  gender,
  name,
  size = "md",
}: {
  gender: string;
  name: string;
  size?: "sm" | "md" | "lg";
}) {
  const isFemale = gender.toLowerCase() === "female";
  const sizeClass =
    size === "sm"
      ? "h-7 w-7 text-sm"
      : size === "lg"
        ? "h-10 w-10 text-xl"
        : "h-8 w-8 text-base";
  return (
    <span
      title={`${name} · ${gender}`}
      aria-label={`${name}, ${gender}`}
      className={`flex shrink-0 items-center justify-center rounded-full border-2 border-white shadow-sm ${sizeClass} ${
        isFemale ? "bg-pink-100 text-pink-700" : "bg-blue-100 text-blue-700"
      }`}
    >
      {isFemale ? "👩" : "👨"} 
    </span>
  );
}

export default function ExternalApplicants({
  onAddApplicants,
  approvedApplicants,
  shortlistedApplicants,
  reloadShortlistedApplicants,
  isReadOnly = false,
}: RoomsAllocationProps) {
  const [showAddApplicantDialog, setShowAddApplicantDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingApplicantIds, setPendingApplicantIds] = useState<string[]>([]);

  const availableApplicants = approvedApplicants.filter(
    (applicant) =>
      !shortlistedApplicants.some((s) => s.id === applicant.id) &&
      (applicant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        applicant.itsNumber.includes(searchQuery)),
  );

  const closeApplicantDialog = () => {
    setShowAddApplicantDialog(false);
    setSearchQuery("");
    setPendingApplicantIds([]);
  };

  const removeApplicantFromShortlist = async (applicantId: string) => {
    const applicant = shortlistedApplicants.find((a) => a.id === applicantId);
    if (!applicant) return;
    try {
      await deleteShortlistedApplicant(applicant.publicId);
      await reloadShortlistedApplicants();
      toast.success("Shortlisted applicant deleted successfully");
    } catch (error) {
      toast.error("Failed to delete shortlisted applicant");
    }
  };

  return (
    <div className="space-y-4 overflow-x-hidden rounded-2xl bg-gradient-to-br from-sky-50/80 via-white to-blue-50/60 p-3 sm:p-4">
      <Card className="gap-0 border-sky-200/70 bg-white/90 shadow-sm">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-stretch gap-2 overflow-x-auto justify-end">
            <Button
              size="sm"
              disabled={isReadOnly}
              onClick={() => {
                if (isReadOnly) return;
                setShowAddApplicantDialog(true);
              }}
              className="h-auto min-w-36 p-2 shrink-0 self-stretch bg-sky-600 text-white hover:bg-sky-700"
            >
              <UserPlus className="mr-1.5 h-4 w-4" /> Add Applicants
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card style={{ gap: 0 }}>
        <CardContent className="p-5">
          <h3 className="mb-3 text-sm font-semibold">
            Added Applicants ({shortlistedApplicants.length})
          </h3>
          {shortlistedApplicants.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-slate-500">
              No applicants added.
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableHeader className="bg-muted/50">
                  <TableRow className="border-b">
                    <TableHead className="px-4 py-3 text-sm font-semibold">
                      Applicant
                    </TableHead>
                    <TableHead className="px-4 py-3 text-sm font-semibold">
                      ITS
                    </TableHead>
                    <TableHead className="px-4 py-3 text-sm font-semibold">
                      Passport
                    </TableHead>
                    <TableHead className="px-4 py-3 text-sm font-semibold">
                      Nationality
                    </TableHead>
                    <TableHead className="px-4 py-3 text-sm text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {shortlistedApplicants.map((applicant) => (
                    <TableRow key={applicant.id} className="border-t">
                      <TableCell className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <GenderAvatar
                            gender={applicant.gender}
                            name={applicant.name}
                          />
                          <div>
                            <div className="text-sm text-gray-900">
                              {applicant.name}
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              {applicant.age}y · {applicant.gender}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        {applicant.itsNumber}
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        {applicant.passportNo}
                      </TableCell>
                      <TableCell className="px-4 py-2">
                        {applicant.nationality}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={isReadOnly}
                          onClick={async () => {
                            if (isReadOnly) return;
                            await removeApplicantFromShortlist(applicant.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {showAddApplicantDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={closeApplicantDialog}
        >
          <div
            className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-sky-100 bg-sky-50/70 p-5">
              <div>
                <h3 className="text-lg font-semibold text-sky-950">
                  Add Applicants
                </h3>
                <p className="mt-0.5 text-sm text-sky-600">
                  Select applicants with submitted visa applications
                </p>
              </div>
              <button
                type="button"
                className="rounded-lg p-2 text-slate-500 hover:bg-white"
                onClick={closeApplicantDialog}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="border-b p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  disabled={isReadOnly}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by name or ITS number..."
                  className="h-10 border-sky-200 pl-10"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {availableApplicants.length === 0 ? (
                <div className="py-12 text-sm text-slate-400">
                  No submitted visa applicants available
                </div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {availableApplicants.map((applicant) => {
                    const selected = pendingApplicantIds.includes(applicant.id);
                    return (
                      <button
                        key={applicant.id}
                        type="button"
                        onClick={() => {
                          if (isReadOnly) return;
                          setPendingApplicantIds((current) =>
                            selected
                              ? current.filter((id) => id !== applicant.id)
                              : [...current, applicant.id],
                          );
                        }}
                        className={`flex items-center gap-3 rounded-xl border p-3 text-left transition ${selected ? "border-sky-400 bg-sky-50" : "border-slate-200 bg-white hover:border-sky-200 hover:bg-sky-50/50"}`}
                      >
                        <GenderAvatar
                          gender={applicant.gender}
                          name={applicant.name}
                          size="lg"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate text-sm font-semibold text-slate-900">
                              {applicant.name}
                            </span>
                            {applicant.hofITS && (
                              <Badge className="bg-indigo-100 px-1.5 py-0 text-[9px] text-indigo-700">
                                {applicant.hofITS}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">
                            ITS {applicant.itsNumber} · {applicant.age}y ·{" "}
                            {applicant.gender}
                          </p>
                        </div>
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${selected ? "border-sky-600 bg-sky-600 text-white" : "border-slate-300"}`}
                        >
                          {selected && <Check className="h-3.5 w-3.5" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between border-t bg-slate-50 p-4">
              <span className="text-xs text-slate-600">
                {pendingApplicantIds.length} selected
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={closeApplicantDialog}
                  disabled={isReadOnly}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={isReadOnly || pendingApplicantIds.length === 0}
                  className="bg-sky-600 text-white hover:bg-sky-700"
                  onClick={() => {
                    onAddApplicants(pendingApplicantIds);
                    closeApplicantDialog();
                  }}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add Selected
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
